import { useState, useRef, useEffect, useCallback } from "react";

// True when the app runs as an installed PWA (home-screen app) or inside the desktop app – not in a normal browser tab.
export const isStandalonePWA = () => {
  try {
    // also true inside the Servora desktop apps (Electron shell exposes window.servoraDesktop) -> same big animated logo
    return !!window.servoraDesktop || window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch { return false; }
};

const SPLASH_KEY = "servora_pwa_splash_seen";
export const splashWanted = () => {
  try {
    if (!isStandalonePWA()) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    return sessionStorage.getItem(SPLASH_KEY) !== "1";
  } catch { return false; }
};

/**
 * Servora logo for login screens: a large logo (browser, installed PWA and desktop apps alike) with a soft entrance, glow and light sweep.
 * On the first login screen of each app launch the logo first appears big in the middle of the screen
 * (rings, glow, light sweep), then shrinks and flies up to its place in the page, and continues with the usual float/glow.
 */
export function PwaLogo({ src = "/logos/platform/black-vertical.png", className = "h-12", width = 67, height = 48, testId }) {
  const [phase, setPhase] = useState(() => (splashWanted() ? "wait" : "done")); // wait → splash → fly → done
  const imgRef = useRef(null);
  const [geo, setGeo] = useState(null);

  const begin = useCallback(() => {
    const el = imgRef.current;
    if (!el) { setPhase("done"); return; }
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) { setPhase("done"); return; }
    const vw = window.innerWidth, vh = window.innerHeight;
    const startW = Math.min(vw * 0.62, 300);
    setGeo({
      left: r.left, top: r.top, width: r.width, height: r.height,
      dx: vw / 2 - (r.left + r.width / 2), dy: vh / 2 - (r.top + r.height / 2), s: startW / r.width,
    });
    try { sessionStorage.setItem(SPLASH_KEY, "1"); } catch { /* noop */ }
    setPhase("splash");
  }, []);

  // Safety net: if the image never reports loaded, skip the intro
  useEffect(() => {
    if (phase !== "wait") return undefined;
    const t = setTimeout(() => setPhase(p => (p === "wait" ? "done" : p)), 2500);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase === "splash") {
      const t = setTimeout(() => setPhase("fly"), 1200);
      return () => clearTimeout(t);
    }
    if (phase === "fly") {
      const t = setTimeout(() => setPhase("done"), 850);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase]);

  const intro = phase !== "done";
  return (
    <div className="pwa-logo" data-testid={testId || "pwa-logo"}>
      <span className="pwa-logo-glow" aria-hidden="true" style={intro ? { visibility: "hidden" } : undefined} />
      <div className={`pwa-logo-body${phase === "done" && geo ? " pwa-landed" : ""}`} style={intro ? { visibility: "hidden", animation: "none" } : undefined}>
        <img ref={imgRef} src={src} alt="Servora" className="pwa-logo-img"
             onLoad={() => { if (phase === "wait") begin(); }} />
        {!intro && <span className="pwa-logo-shine" aria-hidden="true" style={{ WebkitMaskImage: `url(${src})`, maskImage: `url(${src})` }} />}
      </div>
      {phase === "wait" && <div className="pwa-splash" aria-hidden="true" />}
      {(phase === "splash" || phase === "fly") && geo && (
        <div className={`pwa-splash ${phase === "fly" ? "pwa-splash-fly" : ""}`} aria-hidden="true">
          <div className="pwa-splash-logo" style={{
            left: geo.left, top: geo.top, width: geo.width, height: geo.height,
            "--dx": `${geo.dx}px`, "--dy": `${geo.dy}px`, "--s": geo.s,
          }}>
            <span className="pwa-splash-ring" />
            <span className="pwa-splash-ring pwa-splash-ring-2" />
            <span className="pwa-splash-glow" />
            <img src={src} alt="" className="pwa-splash-img" />
            <span className="pwa-splash-shine" style={{ WebkitMaskImage: `url(${src})`, maskImage: `url(${src})` }} />
          </div>
        </div>
      )}
    </div>
  );
}
